import styles from "./AboutSpecTable.module.scss";

/**
 * Product template — "About <product>" band (Figma `4669-6428` / mobile
 * `5596-19378`): eyebrow, heading, intro paragraphs, then a grid of
 * label/value specification cards.
 *
 * The spec values are FACTUAL product data (CAS number, molecular weight,
 * appearance…) and are sourced from the live product record, not authored — see
 * the type config. Nothing here supplies a default value, so a missing field
 * renders as a missing card rather than as an invented one.
 *
 * `body` takes a string OR an array of strings, the same convention as
 * `PartnerBand`/`CalloutBand`/`NetworkSection`: Figma splits this intro into two
 * paragraphs on desktop and merges them on mobile.
 *
 * An item may set `full: true` to span the whole row at BOTH breakpoints — Figma
 * gives the Synonyms card the full 1278px width because its value is a long comma
 * list. `wide: true` spans on MOBILE ONLY: mobile `5596-20558` pairs most specs
 * 2-up but runs Chemical Name, Chemical Formula and Boiling Point full-width,
 * while desktop renders all three as ordinary 1/3-width cards. Items may also
 * carry `tags`, rendered as the pill row inside that card.
 */
export default function AboutSpecTable({
  eyebrow,
  heading,
  body,
  items = [],
  columns = 3,
  surface = "grey",
}) {
  if (!items.length && !heading) return null;
  const paras = Array.isArray(body) ? body : body ? [body] : [];

  return (
    <section className={`${styles.section} ${styles[surface]}`}>
      <div className={styles.content}>
        {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
        {heading && <h2 className={styles.heading}>{heading}</h2>}

        {paras.map((p, i) => (
          <p className={styles.para} key={i}>
            {p}
          </p>
        ))}

        {items.length > 0 && (
          <dl className={styles.grid} style={{ "--cols": columns }}>
            {items.map((item, i) => (
              <div
                className={[
                  styles.card,
                  item.full ? styles.cardFull : "",
                  item.wide ? styles.cardWide : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                key={i}
              >
                <dt className={styles.label}>{item.label}</dt>
                <dd className={styles.value}>{item.value}</dd>

                {item.tags?.length > 0 && (
                  <ul className={styles.tags}>
                    {item.tags.map((tag, j) => (
                      <li className={styles.tag} key={j}>
                        {tag}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}
